/**
 * The Next gate on the eSignature Summary step (INFLOWS-188).
 *
 * Pure decision logic, deliberately split out of {@link OneSpanESignStep}: the
 * rule below is the thing that needs a test, and keeping it free of `t()` and
 * React lets it be tested as a table of (status, mode) → verdict rather than
 * through a render with a mocked API client and a mocked session.
 *
 * <h3>What "approval" means</h3>
 * Confirmed by BMO 2026-10-06: approval means *a package can actually be
 * delivered to the address*. That makes the bar deliverability, not
 * reputation — so `CatchAll`, `SpamTrap` and `DoNotMail` all PASS, keeping
 * their amber warn glyph. They are judgements about the mailbox's character,
 * and OneSpan will still deliver to them.
 *
 * `Invalid` fails, as it always has.
 *
 * <h3>What changed: `Unknown` no longer passes for an advisor</h3>
 * `Unknown` is not a verdict — it is the ABSENCE of one. Every failure mode
 * upstream collapses into it: ZeroBounce unreachable, API key missing from
 * Secrets Manager, a 429 from our own rate limiter, a 4xx from a bad auth
 * policy, or an address ZeroBounce simply didn't return. The front-end `catch`
 * in `workflowsApi.verifyEmails` maps every transport failure to it too.
 *
 * It used to pass, which meant the hard-block could be defeated by any outage
 * — including a silent one, like an expired key nobody had noticed. For the
 * ADVISOR runtime it now BLOCKS: an advisor cannot leave this step until
 * ZeroBounce has actually spoken about every signatory address.
 *
 * PREVIEW is deliberately untouched. An originator inspecting their own
 * template is not sending anything to anyone, and hard-stopping the builder on
 * a ZeroBounce outage would make the authoring surface unusable for a reason
 * that has nothing to do with authoring.
 *
 * Note that a public customer never reaches any of this — `OneSpanESign` is in
 * `PUBLIC_HIDDEN_STEP_TYPES`, so `mode === 'advisor'` here really is the
 * advisor runtime and not the shell's public-maps-to-advisor narrowing.
 */

import type { EmailVerifyResult, EmailVerifyStatus } from '@/api/workflows';

import type { RendererMode } from './formShellMode';

/**
 * Can a signing package be delivered to an address with this verdict?
 *
 * The one place the pass/fail bar is written down. `Unknown` is absent here on
 * purpose — see {@link eSignEmailGate} for why it is handled as a separate
 * case rather than folded in as another `false`.
 */
export function isDeliverableVerdict(status: EmailVerifyStatus): boolean {
  return status !== 'Invalid' && status !== 'Unknown';
}

/**
 * Why Next is blocked, or `ok` when it isn't.
 *
 * A discriminated kind rather than a finished string so this module stays
 * i18n-free; {@link OneSpanESignStep} maps the kind onto a translation key.
 * `count` is the number of offending addresses, used for the plural form.
 */
export type ESignEmailGateVerdict =
  | { kind: 'ok' }
  /** A verification request is still in flight. */
  | { kind: 'checking' }
  /** ZeroBounce says these addresses are not deliverable. */
  | { kind: 'invalid'; count: number }
  /** Advisor only — ZeroBounce never returned a usable verdict for these. */
  | { kind: 'unverified'; count: number };

/**
 * Decide whether the advisor may leave the eSignature Summary step.
 *
 * Order matters and is not arbitrary:
 *
 *  1. **In flight** wins over everything. Without it a fast clicker could hit
 *     Next in the render between the step mounting and the first result
 *     landing, slipping past the gate entirely.
 *  2. **Invalid** outranks unverified, because it is the actionable one: if an
 *     advisor has both a typo'd address and an unreachable ZeroBounce, telling
 *     them to fix the typo is the message that moves them forward.
 *  3. **Unverified** (advisor only) is the new terminal case.
 *
 * @param emails Resolved signatory addresses, already trimmed + lowercased and
 *   deduped — the same keys `results` is keyed by.
 * @param results Verdicts so far, keyed by normalized address. An address
 *   missing from this map has no verdict, which is treated exactly like an
 *   explicit `Unknown`: both mean "ZeroBounce did not tell us".
 * @param verifying True while a batch request is outstanding.
 */
export function eSignEmailGate({
  mode,
  emails,
  results,
  verifying,
}: {
  mode: RendererMode;
  emails: readonly string[];
  results: Record<string, EmailVerifyResult | undefined>;
  verifying: boolean;
}): ESignEmailGateVerdict {
  // No mapped signatory addresses yet — nothing to verify, nothing to block.
  if (emails.length === 0) return { kind: 'ok' };

  if (verifying) return { kind: 'checking' };

  const invalid = emails.filter((e) => results[e]?.status === 'Invalid').length;
  if (invalid > 0) return { kind: 'invalid', count: invalid };

  // Everything below is the no-verdict case. An address with no entry and an
  // address answered `Unknown` are the same situation seen from two angles —
  // the response never arrived, or it arrived carrying no verdict.
  const unverified = emails.filter((e) => {
    const r = results[e];
    return !r || r.status === 'Unknown';
  }).length;
  if (unverified === 0) return { kind: 'ok' };

  // Preview keeps its historical behaviour: a missing result reads as "still
  // working on it" and an explicit Unknown passes, so an originator is never
  // stranded in the builder by an outage they cannot fix.
  if (mode !== 'advisor') {
    const missing = emails.filter((e) => !results[e]).length;
    return missing > 0 ? { kind: 'checking' } : { kind: 'ok' };
  }

  return { kind: 'unverified', count: unverified };
}
